import PrimaryInnerGlowButton, { PrimaryDefaultButton } from '@/components/buttons/primary';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import { VisuallyHidden } from '@/components/Helpers';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import { PressPageQuery } from '@/graphql';
import PressPageQueryDocument from '@/graphql/queries/PressPage.gql';
import { getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import { getStaticPageProps } from '@/lib/next';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { blackScrollbars } from '@/styles/components/scrollbars';
import {
  applyBreakpointNegativeMargins,
  applyBreakpointPadding,
  marginX,
  mediaBreakpointUp,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { Parser as HTMLParser } from 'html-to-react';
import styled from 'styled-components';

// #region - Components

const PageWrapper = styled.section`
  ${paddingY(PRIMARY_NAV_HEIGHT)}
`;

const Hero = styled.header`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto auto;
  row-gap: ${remCalc(48)};
  text-align: center;

  h1 {
    ${customTextSize(40, 42)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(16)};

    small {
      display: block;
      ${customTextSize(16, 26)}
      font-weight: ${FONT_WEIGHTS.NORMAL};
      margin-bottom: ${remCalc(8)};
    }
  }

  p {
    ${customTextSize(18, 29)}
    margin-bottom: ${remCalc(24)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(40)}
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    align-items: center;
    column-gap: ${remCalc(24)};
    text-align: left;

    h1 {
      ${customTextSize(56, 53)}
      margin-bottom: ${remCalc(20)};
    }

    p {
      ${customTextSize(22, 35)}
      margin-bottom: ${remCalc(40)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    h1 {
      ${customTextSize(72, 70)}
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${remCalc(16)};

  ${mediaBreakpointUp('tablet')} {
    flex-direction: row;
  }

  ${mediaBreakpointUp('desktop')} {
    justify-content: flex-start;
  }
`;

const NewsSection = styled.section`
  ${paddingY(40)}

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(60)}
  }
`;

const NewsSectionHeadline = styled.h2`
  ${customTextSize(24, 29)}
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  text-align: center;
  margin-bottom: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    ${customTextSize(32, 38)}
    text-align: left;
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${customTextSize(40, 42)}
    margin-bottom: ${remCalc(32)};
  }
`;

const NewsCardContainer = styled.div`
  display: flex;
  column-gap: ${remCalc(16)};

  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;

  ${applyBreakpointNegativeMargins};
  margin-bottom: ${remCalc(16)};
  ${applyBreakpointPadding};
  padding-bottom: ${remCalc(16)};

  ${mediaBreakpointUp('tablet')} {
    margin-bottom: 0;
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: 0;
    ${marginX(0)}
  }

  ${blackScrollbars};
`;

const NewsCardImage = styled.div`
  position: relative;
  height: ${remCalc(185)};
  width: 100%;
  border-radius: ${remCalc(10)};
  overflow: hidden;
  margin: 0 0 ${remCalc(20)} 0;
  background-color: ${COLORS.GRAY.HIGHLIGHT};

  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(18, 18)}
  letter-spacing: 0.1em;
  text-align: center;
  text-transform: uppercase;
  color: ${COLORS.GRAY.BASE};

  transition: border-radius 0.15s ease-in-out;

  img {
    object-fit: cover;
    object-position: center;
  }

  a {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

const NewsCardTitle = styled.p`
  ${customTextSize(24, 26)}
  margin: 0 0 ${remCalc(16)} 0;
  transition: color 0.15s ease-in-out;
`;

const NewsCard = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  min-height: ${remCalc(400)};
  flex: 1 0 ${remCalc(250)};
  scroll-snap-align: center;
  scroll-snap-stop: always;
  background: transparent;
  border-radius: ${remCalc(10)};
  transition: background 0.15s ease-in-out;

  a {
    color: inherit;
    text-decoration: none;
  }

  &:hover {
    background: ${COLORS.GRAY.HIGHLIGHT};

    ${NewsCardImage} {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    ${NewsCardTitle} {
      color: ${COLORS.BLUE.LOWLIGHT};
    }
  }
`;

const NewsCardContent = styled.div`
  padding: ${remCalc(0, 20, 20)};
`;

const NewsCardEyebrow = styled.p`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  ${customTextSize(12, 15)}
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin: 0 0 ${remCalc(12)} 0;
`;

const ExplainerSection = styled.section`
  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(32)};

  display: flex;
  flex-direction: column;
  row-gap: ${remCalc(48)};

  h2 {
    ${customTextSize(40, 42)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    text-align: center;
    margin-bottom: ${remCalc(20)};
  }

  p {
    ${customTextSize(16, 26)}
    margin-bottom: ${remCalc(20)};

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    display: grid;
    grid-template-columns: repeat(14, 1fr);
    column-gap: ${remCalc(40)};
    align-items: center;
    padding: ${remCalc(60, 0)};

    > div {
      &:first-child {
        grid-column: 2 / span 6;
      }

      &:last-child {
        grid-column: 8 / span 6;
      }
    }

    h2 {
      text-align: left;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    column-gap: ${remCalc(60)};
  }
`;

interface CalloutBlockProps {
  backgroundColor: string;
}

const CalloutBlock = styled.div<CalloutBlockProps>`
  background-color: ${(props) => props.backgroundColor ?? COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};
  padding: ${remCalc(32)};
  display: flex;
  flex-direction: column;
  gap: ${remCalc(36)};

  p {
    ${customTextSize(32, 38)}
    text-align: center;
    color: ${COLORS.WHITE};
  }

  ${ButtonGroup} {
    margin: 0;

    ${PrimaryDefaultButton} {
      --button-color: ${COLORS.WHITE};
      --button-text-color: ${COLORS.BLACK};
      --button-color-hover: ${(props) =>
        props.backgroundColor === COLORS.BLACK ? COLORS.BLUE.LOWLIGHT : COLORS.BLACK};
      --button-text-color-hover: ${COLORS.WHITE};
    }

    ${SecondaryDefaultButton} {
      --button-color: ${COLORS.WHITE};
      --button-text-color: ${COLORS.WHITE};
      --button-color-hover: ${(props) =>
        props.backgroundColor === COLORS.BLACK ? COLORS.BLUE.LOWLIGHT : COLORS.BLACK};
      --button-text-color-hover: ${COLORS.WHITE};
    }
  }

  & + & {
    margin-top: ${remCalc(40)};
  }

  ${mediaBreakpointUp('desktop')} {
    flex-direction: row;
    align-items: center;
    gap: ${remCalc(64)};
    padding: ${remCalc(48, 60)};

    p {
      text-align: left;
      ${customTextSize(40, 48)}
    }

    ${ButtonGroup} {
      margin: 0 0 0 auto;
      flex: 0 0 auto;
    }
  }
`;

// #endregion - Components

const htmlToReactParser = new HTMLParser();

const PressPage: PageTemplate<PressPageQuery> = ({ entry, latestNews }) => {
  const heroImage = getSingle(entry?.coverImage);
  const explainerImage = getSingle(entry?.explainerImage);
  const heroPrimaryCta = getSingleLink(entry?.heroPrimaryCta);
  const heroSecondaryCta = getSingleLink(entry?.heroSecondaryCta);
  const pressKitCta = getSingleLink(entry?.pressKitCta);
  const contactPrimaryCta = getSingleLink(entry?.contactPrimaryCta);
  const contactSecondaryCta = getSingleLink(entry?.contactSecondaryCta);

  return (
    <PageWrapper>
      <Container>
        <Hero>
          <div>
            <h1>
              <small>
                Press<VisuallyHidden>:</VisuallyHidden>
              </small>{' '}
              {entry?.heroPlainText ?? ''}
            </h1>
            {htmlToReactParser.parse(entry?.heroParagraph ?? '')}

            <ButtonGroup>
              {heroPrimaryCta ? (
                <Link href={heroPrimaryCta.url}>
                  <PrimaryInnerGlowButton
                    as="a"
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        location: 'Hero',
                        element_label: `Primary CTA | ${maybeNotSet(heroPrimaryCta.text)}`,
                      });
                    }}
                  >
                    {heroPrimaryCta.text}
                  </PrimaryInnerGlowButton>
                </Link>
              ) : null}

              {heroSecondaryCta ? (
                <Link href={heroSecondaryCta.url}>
                  <SecondaryDefaultButton
                    as="a"
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        location: 'Hero',
                        element_label: `Secondary CTA | ${maybeNotSet(heroSecondaryCta.text)}`,
                      });
                    }}
                  >
                    {heroSecondaryCta.text}
                  </SecondaryDefaultButton>
                </Link>
              ) : null}
            </ButtonGroup>
          </div>

          <div>
            {heroImage && <Image src={heroImage?.url!} width={heroImage.width!} height={heroImage.height!} alt="" />}
          </div>
        </Hero>

        {!!latestNews?.length && (
          <NewsSection>
            <NewsSectionHeadline>Latest news</NewsSectionHeadline>

            <NewsCardContainer>
              {latestNews?.filter(notEmpty).map((article, i) => {
                const onClick = () => {
                  gtm.track('click', {
                    element_type: 'link',
                    element_label: `Article Card | Position ${i + 1} | ${maybeNotSet(article.title)}`,
                    location: 'Latest News Section',
                  });
                };

                const image = getSingle(article.coverImage);

                return (
                  <NewsCard key={article.id}>
                    {!!image && (
                      <NewsCardImage>
                        <Image src={image.url!} alt={image.altText ?? ''} fill />

                        <Link href={article.url ?? '#'}>
                          <a onClick={onClick} />
                        </Link>
                      </NewsCardImage>
                    )}

                    <NewsCardContent>
                      <NewsCardEyebrow>Article</NewsCardEyebrow>

                      <Link href={article.url ?? '#'}>
                        <a onClick={onClick}>
                          <NewsCardTitle>{article.title ?? ''}</NewsCardTitle>
                        </a>
                      </Link>

                      {article.summary && <p>{article.summary}</p>}
                    </NewsCardContent>
                  </NewsCard>
                );
              })}
            </NewsCardContainer>
          </NewsSection>
        )}

        <ExplainerSection>
          <div>
            <h2>{entry?.explainerHeading ?? ''}</h2>
            {htmlToReactParser.parse(entry?.explainerContent ?? '')}
          </div>

          <div>
            {explainerImage && (
              <Image src={explainerImage?.url!} width={explainerImage.width!} height={explainerImage.height!} alt="" />
            )}
          </div>
        </ExplainerSection>

        {!!entry?.pressArticles?.length && (
          <NewsSection>
            <NewsSectionHeadline>{entry.pressArticlesHeading ?? ''}</NewsSectionHeadline>

            <NewsCardContainer>
              {entry.pressArticles?.filter(notEmpty).map((article, i) => {
                const onClick = () => {
                  gtm.track('click', {
                    element_label: `Article Card | Position ${i + 1} | ${maybeNotSet(article.articleTitle)}`,
                    location: `${entry.pressArticlesHeading ?? 'Press Articles'} Section`,
                    element_type: 'link',
                  });
                };

                const logo = getSingle(article.publisherLogo);

                return (
                  <NewsCard key={article.id}>
                    <NewsCardImage>
                      {logo ? (
                        <Image
                          src={logo.url!}
                          width={logo.width! * 1.5}
                          height={logo.height! * 1.5}
                          alt={article.publisherName ?? ''}
                        />
                      ) : (
                        article.publisherName
                      )}

                      <Link href={article.articleUrl ?? '#'}>
                        <a onClick={onClick} target="_blank" />
                      </Link>
                    </NewsCardImage>

                    <NewsCardContent>
                      <NewsCardEyebrow>{article.publisherName}</NewsCardEyebrow>

                      <Link href={article.articleUrl ?? '#'}>
                        <a onClick={onClick}>
                          <NewsCardTitle>{article.articleTitle ?? ''}</NewsCardTitle>
                        </a>
                      </Link>
                    </NewsCardContent>
                  </NewsCard>
                );
              })}
            </NewsCardContainer>
          </NewsSection>
        )}

        {pressKitCta ? (
          <CalloutBlock backgroundColor={COLORS.RED.BASE}>
            <p>{entry?.pressKitHeading ?? ''}</p>

            <ButtonGroup>
              <Link href={pressKitCta.url}>
                <PrimaryDefaultButton
                  as="a"
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      location: 'Press Kit CTA Block',
                      element_label: `Primary CTA | ${maybeNotSet(pressKitCta.text)}`,
                    });
                  }}
                >
                  {pressKitCta.text}
                </PrimaryDefaultButton>
              </Link>
            </ButtonGroup>
          </CalloutBlock>
        ) : null}

        {contactPrimaryCta ? (
          <CalloutBlock backgroundColor={COLORS.BLACK}>
            <p>{entry?.contactHeading ?? ''}</p>
            <ButtonGroup>
              <Link href={contactPrimaryCta.url}>
                <PrimaryDefaultButton
                  as="a"
                  onClick={() => {
                    gtm.track('click', {
                      element_type: 'link',
                      element_label: `Primary CTA | ${maybeNotSet(contactPrimaryCta.text)}`,
                      location: 'Contact CTA Block',
                    });
                  }}
                >
                  {contactPrimaryCta.text}
                </PrimaryDefaultButton>
              </Link>

              {contactSecondaryCta ? (
                <Link href={contactSecondaryCta.url}>
                  <SecondaryDefaultButton
                    as="a"
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        location: 'Contact CTA Block',
                        element_label: `Secondary CTA | ${maybeNotSet(contactSecondaryCta.text)}`,
                      });
                    }}
                  >
                    {contactSecondaryCta.text}
                  </SecondaryDefaultButton>
                </Link>
              ) : null}
            </ButtonGroup>
          </CalloutBlock>
        ) : null}
      </Container>
    </PageWrapper>
  );
};

PressPage.template = MasterTemplate;

export default PressPage;

export const getStaticProps = getStaticPageProps('press', async ({ client }) => {
  const props = await client.request<PressPageQuery>(PressPageQueryDocument);

  return { props };
});
